import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Stethoscope, Loader, AlertCircle, Video, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ChatBox from '../../components/common/ChatBox';
import { useVideoCall } from '../../context/VideoCallContext';
import PermissionsAlert from '../../components/common/PermissionsAlert';

// Base URL del backend (misma lógica que services/api.js) — usamos axios directo
// para NO disparar el interceptor global que redirige a /login ante un 401.
const getApiUrl = () =>
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Página pública que verifica el magic link de WhatsApp y, si es válido, abre
// una sesión LIMITADA para entrar directo al chat/video de esa cita, sin login.
function WhatsappAccessPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithScopedSession, logout } = useAuth();
  const { openVideoCall } = useVideoCall();

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [message, setMessage] = useState('');
  const [appointment, setAppointment] = useState(null);
  const [role, setRole] = useState('patient');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${getApiUrl()}/auth/whatsapp-access/${token}`);
        if (!mounted) return;
        const data = res.data?.data;
        loginWithScopedSession(data.sessionToken, data.user);
        setAppointment(data.appointment);
        setRole(data.role);
        setStatus('ready');
      } catch (err) {
        if (!mounted) return;
        const msg = err.response?.data?.message
          || 'No pudimos validar tu enlace. Solicita uno nuevo o ingresa desde la app.';
        setMessage(msg);
        setStatus('error');
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Contraparte para ChatBox/VideoCallModal (igual criterio que los dashboards).
  const otherUser = role === 'doctor' ? appointment?.patient : appointment?.doctor;

  const handleExit = () => {
    logout();
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Validando tu acceso...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Enlace no válido</h2>
          <p className="text-gray-600 mb-6">{message}</p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="btn-primary px-6 py-2"
          >
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-7 w-7 text-primary-600" />
            <span className="text-2xl font-bold text-gray-900">DocEnLinea</span>
          </div>
          <button
            type="button"
            onClick={handleExit}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h1 className="text-xl font-bold text-gray-800 mb-1">Tu consulta está lista</h1>
          <p className="text-gray-600 text-sm">
            {appointment?.counterpartName
              ? `Consulta con ${appointment.counterpartName}`
              : 'Consulta médica'}
          </p>

          {/* El chat queda siempre montado abajo y el video se abre encima
              como overlay, así que ya no hay que conmutar entre ambos. */}
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => openVideoCall(appointment, otherUser)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition bg-primary-600 text-white hover:bg-primary-700"
            >
              <Video className="h-4 w-4" /> Entrar a la videollamada
            </button>
          </div>
        </div>

        {appointment && (
          <div className="bg-white rounded-xl shadow overflow-hidden max-h-[70vh] flex flex-col">
            <ChatBox appointment={appointment} otherUser={otherUser} />
          </div>
        )}
      </div>

      <PermissionsAlert />
    </div>
  );
}

export default WhatsappAccessPage;
